import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../services/firestoreService';
import { Timestamp } from 'firebase/firestore';

const EMPTY = {
  procedure: '',
  code: '',
  group: '',
  location: '',
  pabellon: false,
  anesthesiaLevel: '',
  prestaciones: [],
  active: true,
};

const TabProcedures = ({ aranceles = [] }) => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadProcedures();
  }, []);

  const loadProcedures = async () => {
    setLoading(true);
    try {
      const data = await getCollection('procedures');
      setProcedures(
        data.sort((a, b) => (a.procedure || '').localeCompare(b.procedure || '', 'es'))
      );
    } catch (e) {
      setError('Error al cargar procedimientos: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setError('');
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      procedure: p.procedure || '',
      code: p.code || '',
      group: p.group || '',
      location: p.location || '',
      pabellon: !!p.pabellon,
      anesthesiaLevel: p.anesthesiaLevel || '',
      prestaciones: Array.isArray(p.prestaciones) ? p.prestaciones : [],
      active: p.active !== false,
    });
    setError('');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setBool = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const handlePrestacionesChange = (e) => {
    const ids = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    const selected = aranceles
      .filter((a) => ids.includes(a.id))
      .map((a) => ({ id: a.id, prestacion: a.prestacion }));
    setForm((f) => ({ ...f, prestaciones: selected }));
  };

  const validate = () => {
    if (!form.procedure.trim()) return 'El nombre del procedimiento es obligatorio.';
    return '';
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, updatedAt: Timestamp.now() };
      if (editing) {
        await updateDocument('procedures', editing, payload);
      } else {
        await addDocument('procedures', { ...payload, createdAt: Timestamp.now() });
      }
      setOpen(false);
      await loadProcedures();
    } catch (e) {
      setError('Error al guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    try {
      await deleteDocument('procedures', p.id);
      setDeleteConfirm(null);
      await loadProcedures();
    } catch (e) {
      setError('Error al eliminar: ' + e.message);
    }
  };

  const selectedIds = form.prestaciones.map((p) => p.id);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Nuevo procedimiento
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>Procedimiento</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell>Ubicación</TableCell>
              <TableCell>Anestesia</TableCell>
              <TableCell>Pabellón</TableCell>
              <TableCell>Presupuesto</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {procedures.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay procedimientos registrados.
                </TableCell>
              </TableRow>
            )}
            {procedures.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>{p.procedure}</TableCell>
                <TableCell>{p.code || '—'}</TableCell>
                <TableCell>{p.group || '—'}</TableCell>
                <TableCell>{p.location || '—'}</TableCell>
                <TableCell>{p.anesthesiaLevel || '—'}</TableCell>
                <TableCell>
                  <Switch checked={!!p.pabellon} size="small" disabled />
                </TableCell>
                <TableCell>
                  {p.prestaciones?.length ? (
                    <Chip
                      label={`${p.prestaciones.length} prestaciones`}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(p)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteConfirm(p)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Editar procedimiento' : 'Nuevo procedimiento'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Procedimiento *"
              value={form.procedure}
              onChange={setField('procedure')}
              fullWidth
              required
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Código"
                value={form.code}
                onChange={setField('code')}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <TextField
                label="Grupo"
                value={form.group}
                onChange={setField('group')}
                sx={{ flex: 1, minWidth: 180 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Ubicación"
                value={form.location}
                onChange={setField('location')}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <TextField
                label="Nivel de anestesia"
                value={form.anesthesiaLevel}
                onChange={setField('anesthesiaLevel')}
                sx={{ flex: 1, minWidth: 180 }}
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Prestaciones del presupuesto</InputLabel>
              <Select
                multiple
                value={selectedIds}
                onChange={handlePrestacionesChange}
                input={<OutlinedInput label="Prestaciones del presupuesto" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const a = aranceles.find((x) => x.id === id);
                      return <Chip key={id} label={a?.prestacion || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {aranceles.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    <Checkbox checked={selectedIds.includes(a.id)} />
                    {a.prestacion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={form.pabellon} onChange={setBool('pabellon')} />}
              label="Requiere pabellón"
            />
            <FormControlLabel
              control={<Switch checked={form.active} onChange={setBool('active')} />}
              label="Activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Eliminar procedimiento</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar el procedimiento <strong>{deleteConfirm?.procedure}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabProcedures;
